import React from 'react'
import Bus from '../bus'
import SportManager from '../managers/sport'
import SubscriptionManager from '../connection/subscription'

// import logger from '../utils/logger'
import { isEmpty, isArray } from 'lodash'
import { SportsbookError } from '../error'
import {
  // removeFromArrayById,
  removeFromCollection,
  existInCollection,
  logger
} from '../utils'

import { useApplicationState } from './root'
import { useSportsbookState } from './sportsbook'
import { useSettings } from './settings'

import {
  CATEGORY_TOURNAMENT_REMOVE,

  TOURNAMENT_EVENT_ADD,
  TOURNAMENT_EVENT_REMOVE,
  TOURNAMENT_STATE_UPDATE
} from '../actions'

export const TournamentContext = React.createContext()

export const TournamentProvider = props => {
  const {
    tournament,
    children
  } = props

  const [
    tournamentState
  ] = createTournamentState(tournament)

  return (
    <TournamentContext.Provider value={tournamentState}>
      {children}
    </TournamentContext.Provider>
  )
}

export const tournamentInitialState = {
  events: [],
  loading: true,
  initialized: false
}

export function useTournamentState () {
  return React.useContext(TournamentContext) || tournamentInitialState
}

export const tournamentReducer = (state, action) => {
  switch (action.type) {
    case TOURNAMENT_STATE_UPDATE:
      return {
        ...state,
        ...action.payload
      }
    case TOURNAMENT_EVENT_ADD:
      // If no event with payload.event.id - adding event
      if (existInCollection(state.events, action.payload.eventId)) {
        return state
      }

      return {
        ...state,
        events: {
          ...state.events,
          [action.payload.eventId]: action.payload.event
        }
      }
    case TOURNAMENT_EVENT_REMOVE:
      // If no event with payload.eventId - skipping
      if (existInCollection(state.events, action.payload.eventId)) {
        return {
          ...state,
          events: removeFromCollection(state.events, action.payload.eventId)
        }
      }

      return state
    default:
      return state
  }
}

const createTournamentInitialState = tournament => {
  return {
    ...tournament,
    ...tournamentInitialState
  }
}

export const createTournamentState = initialTournamentState => {
  const [tournament, tournamentDispatch] = React.useReducer(
    tournamentReducer,
    initialTournamentState,
    createTournamentInitialState
  )

  const [sportsbookState] = useSportsbookState()
  const {
    sportService,
    sportServices,
    fromDate,
    toDate
  } = sportsbookState

  const { connection } = useApplicationState()
  const { lang } = useSettings()

  /*
    LoadingTournament Events Data
  */
  React.useEffect(
    () => {
      if (!tournament.id) { return }

      tournamentDispatch({
        type: TOURNAMENT_STATE_UPDATE,
        payload: {
          loading: true,
          // initialized: false,
          error: null
        }
      })

      SportManager.findEventsByTournaments({
        filter: {
          sportService,
          sportServices,
          sportId: tournament.sportId,
          categoryId: tournament.categoryId,
          tournamentId: tournament.id,
          /*
            Provider used only in sportsbook list views
            Applying filter for only main market types
          */
          onlyMainMarketTypes: true,
          fromDate,
          toDate
        }
      })
        .then(res => {
          const data = res.data || {}
          const tournamentData = data[tournament.id]

          if (isEmpty(tournamentData)) {
            throw new SportsbookError('No tournament events found', res)
          }

          return tournamentData
        })
        .then(resolvedTournamentEvents => {
          tournamentDispatch({
            type: TOURNAMENT_STATE_UPDATE,
            payload: {
              events: resolvedTournamentEvents.events,
              loading: false,
              initialized: true
            }
          })

          // Broadcasting resolved tournament events
          Bus.send({
            event: Bus.events.sport.tournamentEventsLoaded,
            data: resolvedTournamentEvents
          })
        })
        .catch(error => {
          logger.debug(
            `[tournament:${tournament.id}]`,
            'unable resolve tournament events',
            error
          )

          tournamentDispatch({
            type: TOURNAMENT_STATE_UPDATE,
            payload: {
              events: tournamentInitialState.events,
              error,
              loading: false,
              initialized: true
            }
          })
        })
    },
    [connection.id, tournament.id, lang, fromDate, toDate]
  )

  // Tournament self destruction
  React.useEffect(
    () => {
      if (tournament.initialized && isEmpty(tournament.events)) {
        logger.debug(
          `[tournament:${tournament.id}]`,
          `self destructing initialized tournament in category#${tournament.categoryId} state`
        )

        //  Removing tournament from category state
        SportManager.dataBroadcaster.emit(
          `category:${tournament.categoryId}`,
          {
            type: CATEGORY_TOURNAMENT_REMOVE,
            payload: {
              id: tournament.id
            }
          }
        )

        // Removing initialized flag from tournament state
        tournamentDispatch({
          type: TOURNAMENT_STATE_UPDATE,
          payload: {
            initialized: false
          }
        })
      }
    },
    [
      tournament.events,
      tournament.initialized
    ]
  )

  // Special handler for event sport service updates in tournament scope
  React.useEffect(
    () => {
      if (!tournament.id) { return }

      const handleTournamentEventsSportServiceUpdates = data => {
        const { events } = data

        if (isEmpty(events)) { return }

        for (const event of Object.values(events)) {
          // Valdating provided sportsbook sport services
          // and defining is sport service match events sport service
          const matchSportService = (isArray(sportServices) && (sportServices.length > 0))
            ? sportServices.includes(event.sportService)
            : sportService && (sportService === event.sportService)

          // If event sport service same as active sportsbook sport service
          if (matchSportService) {
            // Resolving event by id
            SportManager.findEvent(event.id)
              .then(event => {
                if (isEmpty(event)) { return }
                tournamentDispatch({
                  type: TOURNAMENT_EVENT_ADD,
                  payload: {
                    eventId: event.id,
                    event
                  }
                })
              })
          } else {
            tournamentDispatch({
              type: TOURNAMENT_EVENT_REMOVE,
              payload: {
                eventId: event.id
              }
            })
          }
        }
      }

      SportManager.statusBroadcaster.subscribeToTournamentUpdates(
        tournament.id,
        handleTournamentEventsSportServiceUpdates
      )

      return () => {
        SportManager.statusBroadcaster.unsubscribeFromTournamentUpdates(
          tournament.id,
          handleTournamentEventsSportServiceUpdates
        )
      }
    },
    [tournament.id]
  )

  /*
    Subscribing (to DataUpdatesBroadcaster) on Tournament Updates
  */
  React.useEffect(
    () => {
      if (!tournament.id) { return }

      // Subscription topics in tournament scope
      const tournamentSubscriptionTopics = [
        {
          topic: SubscriptionManager.subscriptionTopics.EXPERIMENTAL_TOURNAMENT,
          key: tournament.id
        },
        {
          topic: SubscriptionManager.subscriptionTopics.TOURNAMENT_SPORT_SERVICE_UPDATE,
          key: tournament.id
        }
      ]

      // Update handler
      const tournamentUpdateHandler = (action) => (
        tournamentDispatch(action)
      )

      // Internal subscription in app
      SportManager.dataBroadcaster.subscribeToTournamentUpdates(
        tournament.id,
        tournamentUpdateHandler
      )

      // External subscription on Notifier Service
      SubscriptionManager.notifierSubscribe(tournamentSubscriptionTopics)

      return () => {
        // External subscription on Notifier Service
        SubscriptionManager.notifierUnsubscribe(tournamentSubscriptionTopics)

        // Internal subscription in app
        SportManager.dataBroadcaster.unsubscribeFromTournamentUpdates(
          tournament.id,
          tournamentUpdateHandler
        )
      }
    },
    [tournament.id]
  )

  return [
    tournament,
    tournamentDispatch
  ]
}
